// src/components/rcv/AsociacionesTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Grid, TextField,
  Button, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Box, FormControlLabel, Switch,
  Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Accordion, AccordionSummary, AccordionDetails, Typography, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Search, Download, GetApp, ExpandMore, AttachFile } from '@mui/icons-material';

// Componentes compartidos
import { CardContainer } from '../shared';

// Importar componentes
import DocumentosList from './asociaciones/DocumentosList';
import FormularioAsociacion from './asociaciones/FormularioAsociacion';
import EntidadDialog from './asociaciones/EntidadDialog';

// Importar servicios y utilidades
import { asociacionesService } from '../../services/asociacionesService';
import { documentosService } from '../../services/documentosService';
import { catalogosService } from '../../services/catalogosService';
import { dtedocService } from '../../services/dtedocService';

const AsociacionesTab = () => {
  // Estado para empresa activa y periodos
  const { empresaActiva } = useSelector(state => state.empresa);
  const [rutEmpresa, setRutEmpresa] = useState('');
  const [dvEmpresa, setDvEmpresa] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [periodos, setPeriodos] = useState([]);
  
  // Estado para carga de datos
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estado para documentos y filtros
  const [documentos, setDocumentos] = useState([]);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [documentosFiltrados, setDocumentosFiltrados] = useState([]);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [mapaAsociaciones, setMapaAsociaciones] = useState({});
  const [mostrarSoloNoAsociados, setMostrarSoloNoAsociados] = useState(false);
  const [mostrarSoloConAdjunto, setMostrarSoloConAdjunto] = useState(false);
  const [conAdjunto, setConAdjunto] = useState(new Set());
  
  // Estado para formulario
  const [cuentas, setCuentas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [clasificadores, setClasificadores] = useState([]);
  
  // Estado para diálogo de nueva entidad
  const [openNuevaEntidad, setOpenNuevaEntidad] = useState(false);

  // Estado para exportación
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [folioInicial, setFolioInicial] = useState(1000);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [exportaciones, setExportaciones] = useState([]);
  const [cargandoExportaciones, setCargandoExportaciones] = useState(false);
  const [pendientesExportar, setPendientesExportar] = useState(null);
  
  // Cargar catálogos (cuentas, centros, clasificadores) filtrados por empresa
  const cargarCatalogos = useCallback(async () => {
    if (!rutEmpresa) {
      console.warn("No hay empresa seleccionada para cargar catálogos");
      return;
    }

    try {
      const [cuentasData, centrosData, clasificadoresData] = await Promise.all([
        catalogosService.getCuentas(true, rutEmpresa),
        catalogosService.getCentros(true, rutEmpresa),
        catalogosService.getClasificadores(null, true, rutEmpresa)
      ]);

      setCuentas(cuentasData);
      setCentros(centrosData);
      setClasificadores(clasificadoresData);
    } catch (err) {
      console.error("Error al cargar catálogos:", err);
      setError("Error al cargar catálogos contables. Por favor, intente nuevamente.");
    }
  }, [rutEmpresa]);


  // Cargar documentos y asociaciones
  const cargarDocumentos = useCallback(async () => {
    if (!rutEmpresa || !periodo) {
      return;
    }
  
    setLoading(true);
    setError('');
    setSuccess('');
    setDocumentos([]);
    setDocumentosFiltrados([]);
    setDocumentoSeleccionado(null);
    setMapaAsociaciones({});
    setConAdjunto(new Set());

    try {
      // Cargar documentos y asociaciones en paralelo
      const [documentosData, asociacionesData] = await Promise.all([
        documentosService.getDocumentosPeriodo(rutEmpresa, periodo),
        asociacionesService.getAsociaciones(rutEmpresa, periodo)
      ]);

      // Crear mapa de asociaciones
      const mapa = {};
      asociacionesData.forEach(asoc => {
        const key = `${asoc.rut_proveedor}-${asoc.folio}`;
        mapa[key] = asoc;
      });

      setDocumentos(documentosData);
      setDocumentosFiltrados(documentosData);
      setMapaAsociaciones(mapa);

      // Cargar qué movimientos tienen adjuntos (no bloqueante)
      if (documentosData.length > 0) {
        const ids = documentosData.map(d => d.id);
        dtedocService.getMovimientosConAdjunto(ids)
          .then(idsConAdjunto => setConAdjunto(new Set(idsConAdjunto)))
          .catch(() => {}); // silencioso — no crítico
      }
    } catch (err) {
      setError(err.message || 'Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  }, [rutEmpresa, periodo]);
  
  // Inicializar datos
  useEffect(() => {
    generarPeriodos();
  }, []);
  
  // Asignar empresa activa y actualizar catálogos cuando cambia
  useEffect(() => {
    if (empresaActiva) {
      setRutEmpresa(empresaActiva.rut);
      setDvEmpresa(empresaActiva.dv);
    }
  }, [empresaActiva]);

  // Cargar catálogos cuando cambia el RUT de la empresa
  useEffect(() => {
    if (rutEmpresa) {
      cargarCatalogos();
    }
  }, [rutEmpresa, cargarCatalogos]);
  
  // Cargar documentos cuando cambia el periodo
  useEffect(() => {
    if (rutEmpresa && periodo) {
      cargarDocumentos();
    }
  }, [rutEmpresa, periodo, cargarDocumentos]);
  
  // Filtrar documentos (por texto y por estado de asociación)
  useEffect(() => {
    // Primero aplicamos los filtros básicos (por texto)
    let filtrados = documentos;
    
    if (filtroTexto.trim() !== '') {
      const textoBusqueda = filtroTexto.toLowerCase();
      filtrados = documentos.filter(doc => 
        doc.rut_proveedor?.toString().includes(textoBusqueda) ||
        doc.razon_social?.toLowerCase().includes(textoBusqueda) ||
        doc.folio?.toString().includes(textoBusqueda) ||
        doc.tipo_doc?.toLowerCase().includes(textoBusqueda)
      );
    }
    
    // Luego aplicamos el filtro de asociación si está activo
    if (mostrarSoloNoAsociados) {
      filtrados = filtrados.filter(doc => {
        const key = `${doc.rut_proveedor}-${doc.folio}`;
        return !mapaAsociaciones[key];
      });
    }

    // Filtro de adjuntos (muestra solo los que les falta adjunto)
    if (mostrarSoloConAdjunto) {
      filtrados = filtrados.filter(doc => !conAdjunto.has(doc.id));
    }

    setDocumentosFiltrados(filtrados);
  }, [filtroTexto, documentos, mostrarSoloNoAsociados, mapaAsociaciones, mostrarSoloConAdjunto, conAdjunto]);
  
  // Función para generar periodos
  const generarPeriodos = () => {
    // Importar la función de generación de períodos
    import('../../utils/periodos').then(({ generarPeriodos }) => {
      const opciones = generarPeriodos();
      setPeriodos(opciones);
      if (opciones.length > 0) {
        setPeriodo(opciones[0].value);
      }
    });
  };
  

  
  // Manejar actualización exitosa
  const handleAsociacionGuardada = (mensaje) => {
    setSuccess(mensaje);
    // Recargar datos después de un breve retraso
    setTimeout(() => {
      cargarDocumentos();
      cargarPendientesExportar();  // Actualizar el badge de pendientes
    }, 1000);
  };

  // Cargar exportaciones previas
  const cargarExportaciones = useCallback(async () => {
    if (!rutEmpresa || !periodo) {
      return;
    }

    setCargandoExportaciones(true);
    try {
      const data = await asociacionesService.listarExportaciones(rutEmpresa, periodo);
      setExportaciones(data);
    } catch (err) {
      console.error("Error al cargar exportaciones:", err);
    } finally {
      setCargandoExportaciones(false);
    }
  }, [rutEmpresa, periodo]);

  // Cargar información de pendientes de exportar
  const cargarPendientesExportar = useCallback(async () => {
    if (!rutEmpresa || !periodo) {
      return;
    }

    try {
      const data = await asociacionesService.obtenerPendientesExportar(rutEmpresa, periodo);
      setPendientesExportar(data);
    } catch (err) {
      console.error("Error al cargar pendientes:", err);
    }
  }, [rutEmpresa, periodo]);

  // Cargar exportaciones y pendientes cuando cambia el período
  useEffect(() => {
    if (rutEmpresa && periodo) {
      cargarExportaciones();
      cargarPendientesExportar();
    }
  }, [rutEmpresa, periodo, cargarExportaciones, cargarPendientesExportar]);

  // Manejar exportación
  const handleExportar = async () => {
    if (!folioInicial || folioInicial < 1) {
      setError("El folio inicial debe ser mayor a 0");
      return;
    }

    setExportando(true);
    setError("");
    try {
      const resultado = await asociacionesService.exportarAsociaciones({
        rut_empresa: rutEmpresa,
        dv_empresa: dvEmpresa,
        periodo: periodo,
        folio_inicial: parseInt(folioInicial),
        solo_pendientes: soloPendientes
      });

      setSuccess(`Exportación completada: ${resultado.cantidad_movimientos} documentos exportados`);
      setOpenExportDialog(false);

      // Recargar exportaciones
      setTimeout(() => {
        cargarExportaciones();
        cargarPendientesExportar();
      }, 1000);

      // Descargar automáticamente
      if (resultado.archivo_url) {
        const link = document.createElement('a');
        link.href = resultado.archivo_url;
        link.download = true;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setError(err.message || "Error al exportar asociaciones");
    } finally {
      setExportando(false);
    }
  };

  // Descargar exportación
  const handleDescargarExportacion = async (exportacionId) => {
    try {
      const link = document.createElement('a');
      link.href = `/api/v1/rcv/asociaciones/exportaciones/${exportacionId}/descargar`;
      link.download = true;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError("Error al descargar archivo");
    }
  };
  
  return (
    <CardContainer
      title="Asociaciones Contables"
      subheader="Asocie documentos del RCV a cuentas, centros y clasificadores"
    >
        {/* Filtros y búsqueda */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <TextField
                  label="RUT Empresa"
                  placeholder="Ej: 77123456"
                  fullWidth
                  value={rutEmpresa}
                  disabled={true}
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="DV"
                  placeholder="K"
                  fullWidth
                  value={dvEmpresa}
                  disabled={true}
                  size="small"
                  inputProps={{ maxLength: 1 }}
                />
              </Grid>
            </Grid>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Período</InputLabel>
              <Select
                value={periodo}
                label="Período"
                onChange={(e) => setPeriodo(e.target.value)}
              >
                {periodos.map((opcion) => (
                  <MenuItem key={opcion.value} value={opcion.value}>
                    {opcion.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              label="Buscar en documentos"
              placeholder="RUT, razón social, folio..."
              fullWidth
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={cargarDocumentos}
              disabled={loading || !rutEmpresa || !periodo}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              Buscar
            </Button>
          </Grid>
          
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 1, mt: 2, mb: 1, bgcolor: 'background.default', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mostrarSoloNoAsociados}
                        onChange={(e) => setMostrarSoloNoAsociados(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Mostrar solo documentos pendientes de asociar"
                  />
                  <Chip
                    label={mostrarSoloNoAsociados ? "Mostrando solo pendientes" : "Mostrando todos"}
                    color={mostrarSoloNoAsociados ? "primary" : "default"}
                    size="small"
                    variant={mostrarSoloNoAsociados ? "filled" : "outlined"}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mostrarSoloConAdjunto}
                        onChange={(e) => setMostrarSoloConAdjunto(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AttachFile fontSize="small" sx={{ opacity: 0.7 }} />
                        Solo sin adjunto
                      </Box>
                    }
                  />
                </Box>

                {/* Indicador de estado de exportación */}
                {pendientesExportar && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label={`${pendientesExportar.exportadas}/${pendientesExportar.total_asociaciones} Exportadas`}
                      color={pendientesExportar.exportadas === pendientesExportar.total_asociaciones ? "success" : "warning"}
                      size="small"
                      variant="outlined"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<Download />}
                      onClick={() => {
                        setOpenExportDialog(true);
                        setSoloPendientes(false);
                      }}
                      disabled={documentos.length === 0}
                    >
                      Exportar
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Mensajes de error y éxito */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
        )}

        {/* Contenido principal */}
        <Grid container spacing={2}>
          {/* Lista de documentos */}
          <Grid item xs={12} md={6}>
            <DocumentosList
              documentos={documentosFiltrados}
              mapaAsociaciones={mapaAsociaciones}
              documentoSeleccionado={documentoSeleccionado}
              setDocumentoSeleccionado={setDocumentoSeleccionado}
              loading={loading}
              mostrarSoloNoAsociados={mostrarSoloNoAsociados}
              totalDocumentos={documentos.length}
              conAdjunto={conAdjunto}
              rutEmpresa={rutEmpresa}
              onDocumentoCreado={() => {
                cargarDocumentos();
                setSuccess("Documento cargado exitosamente");
              }}
            />
          </Grid>
          
          {/* Formulario de asociación */}
          <Grid item xs={12} md={6}>
            <FormularioAsociacion
              documentoSeleccionado={documentoSeleccionado}
              setDocumentoSeleccionado={setDocumentoSeleccionado}
              mapaAsociaciones={mapaAsociaciones}
              cuentas={cuentas}
              centros={centros}
              clasificadores={clasificadores}
              rutEmpresa={rutEmpresa}
              periodo={periodo}
              onAsociacionGuardada={handleAsociacionGuardada}
              onEntidadDialogOpen={() => setOpenNuevaEntidad(true)}
            />
          </Grid>
        </Grid>

      {/* Diálogo para crear nueva entidad */}
      <EntidadDialog
        open={openNuevaEntidad}
        onClose={() => setOpenNuevaEntidad(false)}
        onEntidadCreada={() => {
          setSuccess('Entidad creada correctamente');
        }}
      />

      {/* Diálogo de exportación */}
      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Exportar Asociaciones</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Se exportarán <strong>TODOS los documentos asociados</strong> del período {periodo}.
            </Alert>

            <TextField
              label="Folio Inicial"
              type="number"
              value={folioInicial}
              onChange={(e) => setFolioInicial(e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Número del primer comprobante a generar"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={soloPendientes}
                  onChange={(e) => setSoloPendientes(e.target.checked)}
                />
              }
              label="Solo los que aún NO han sido exportados"
            />

            <Alert severity="warning">
              {soloPendientes
                ? `✓ Exportación incremental: ${pendientesExportar?.pendientes_exportar || 0} documentos nuevos`
                : `✓ Exportación completa: ${pendientesExportar?.exportadas || 0} documentos asociados (incluyendo los ya exportados)`}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleExportar}
            variant="contained"
            disabled={exportando}
            startIcon={exportando ? <CircularProgress size={20} /> : <GetApp />}
          >
            {exportando ? "Exportando..." : "Exportar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Historial de exportaciones */}
      {exportaciones.length > 0 && (
        <Accordion sx={{ mt: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">
              Historial de Exportaciones ({exportaciones.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell align="right"><strong>Cantidad</strong></TableCell>
                    <TableCell align="right"><strong>Valor Total</strong></TableCell>
                    <TableCell align="center"><strong>Folios</strong></TableCell>
                    <TableCell align="center"><strong>Acción</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportaciones.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>
                        {new Date(exp.fecha_exportacion).toLocaleString('es-CL')}
                      </TableCell>
                      <TableCell align="right">{exp.cantidad_movimientos}</TableCell>
                      <TableCell align="right">
                        ${Number(exp.valor_total).toLocaleString('es-CL', { minimumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="center">
                        {exp.folio_inicial}-{exp.folio_final}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          startIcon={<GetApp />}
                          onClick={() => handleDescargarExportacion(exp.id)}
                          disabled={!exp.archivo_generado}
                        >
                          Descargar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}
    </CardContainer>
  );
};

export default AsociacionesTab;